import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * LocalBest.ai bilingual context (Phase 1 — EN / ES).
 *
 * - Auto-detects `navigator.language` on first visit; if it's Spanish-leaning
 *   we set a soft hint so the LanguagePromptBanner can highlight Español, but
 *   we do NOT auto-switch the page without the visitor's tap.
 * - Persists the chosen language in localStorage so it sticks across visits.
 * - Exposes a tiny `t()` helper for static UI labels (CTAs, prompts, etc.).
 */

const STORAGE_KEY = "localbest:lang";
const BANNER_DISMISSED_KEY = "localbest:lang:banner-dismissed";

const detectBrowserSpanish = () => {
  if (typeof navigator === "undefined") return false;
  const langs = [navigator.language, ...(navigator.languages || [])];
  return langs.some((l) => (l || "").toLowerCase().startsWith("es"));
};

// Static UI strings used on the public listing page. Add more as we expand.
const STRINGS = {
  en: {
    languagePrompt: {
      headline: "This listing is also available in Español",
      body: "Tap below to view it in Spanish.",
      switchToEs: "Switch to Español",
      keepEn: "Keep English",
      dismiss: "Dismiss",
    },
    toggle: { en: "English", es: "Español" },
    cta: {
      askAi: "Ask AI",
      callNow: "Call Now",
      requestHelp: "Request Help",
      bookAppointment: "Book Appointment",
      viewServices: "View Services",
      getOffer: "Get Offer",
      claimRewards: "Claim My Rewards",
      sendMessage: "Send a Message",
      visitWebsite: "Visit Website",
      directions: "Directions",
      share: "Share",
    },
    sections: {
      about: "About",
      services: "Services",
      products: "Products",
      reviews: "Reviews",
      gallery: "Gallery",
      faq: "Frequently Asked Questions",
      offers: "Offers",
      hours: "Hours",
      contact: "Contact",
      liveShorts: "Live Shorts",
    },
    chat: {
      placeholder: "Type your message…",
      greeting: (heroName) => `Hi! I'm ${heroName || "your AI Hero"}. How can I help you today?`,
    },
    leadForm: {
      name: "Your name",
      email: "Email",
      phone: "Phone",
      message: "Message",
      submit: "Send",
      submitting: "Sending…",
      success: "Thanks! We'll be in touch shortly.",
    },
    languageBadge: "Language set to English",
  },
  es: {
    languagePrompt: {
      headline: "Esta empresa también está disponible en Español",
      body: "Toque abajo para verlo en español.",
      switchToEs: "Cambiar a Español",
      keepEn: "Mantener inglés",
      dismiss: "Cerrar",
    },
    toggle: { en: "English", es: "Español" },
    cta: {
      askAi: "Preguntar a la IA",
      callNow: "Llamar Ahora",
      requestHelp: "Solicitar Ayuda",
      bookAppointment: "Agendar Cita",
      viewServices: "Ver Servicios",
      getOffer: "Obtener Oferta",
      claimRewards: "Reclamar Mis Ofertas",
      sendMessage: "Enviar Mensaje",
      visitWebsite: "Visitar Sitio Web",
      directions: "Cómo llegar",
      share: "Compartir",
    },
    sections: {
      about: "Acerca de",
      services: "Servicios",
      products: "Productos",
      reviews: "Reseñas",
      gallery: "Galería",
      faq: "Preguntas Frecuentes",
      offers: "Ofertas",
      hours: "Horario",
      contact: "Contacto",
      liveShorts: "Videos en Vivo",
    },
    chat: {
      placeholder: "Escriba su mensaje…",
      greeting: (heroName) =>
        `Hola, bienvenido. ${heroName ? `Soy ${heroName}. ` : ""}Puedo ayudarle en español. Puede hacerme preguntas sobre esta empresa, reclamar ofertas, solicitar una llamada o pedir ayuda ahora.`,
    },
    leadForm: {
      name: "Su nombre",
      email: "Correo electrónico",
      phone: "Teléfono",
      message: "Mensaje",
      submit: "Enviar",
      submitting: "Enviando…",
      success: "¡Gracias! Nos pondremos en contacto pronto.",
    },
    languageBadge: "Idioma cambiado a Español",
  },
};

const LanguageContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (path) => path,
  browserPrefersSpanish: false,
  shouldShowBanner: false,
  dismissBanner: () => {},
});

export function LanguageProvider({ children }) {
  const [lang, _setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem(STORAGE_KEY) || "en";
  });
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(BANNER_DISMISSED_KEY) === "1";
  });
  const browserPrefersSpanish = useMemo(detectBrowserSpanish, []);

  const setLang = useCallback((next) => {
    const value = next === "es" ? "es" : "en";
    _setLang(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (_) {
      /* private mode */
    }
    // Once the user picks, we never need to show the banner again
    setBannerDismissed(true);
    try {
      localStorage.setItem(BANNER_DISMISSED_KEY, "1");
    } catch (_) {
      /* ignore */
    }
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try {
      localStorage.setItem(BANNER_DISMISSED_KEY, "1");
    } catch (_) {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (path, ...args) => {
      const parts = path.split(".");
      let node = STRINGS[lang] || STRINGS.en;
      for (const p of parts) {
        node = node?.[p];
        if (node === undefined) break;
      }
      if (typeof node === "function") return node(...args);
      if (node === undefined) {
        // Fall back to English
        let en = STRINGS.en;
        for (const p of parts) en = en?.[p];
        return typeof en === "function" ? en(...args) : en ?? path;
      }
      return node;
    },
    [lang]
  );

  // Show banner only when:
  //  - browser leans Spanish
  //  - user hasn't dismissed
  //  - user is currently on English (else there's nothing to suggest)
  const shouldShowBanner = browserPrefersSpanish && !bannerDismissed && lang === "en";

  useEffect(() => {
    // Reflect the language on <html lang> for accessibility & SEO
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  return (
    <LanguageContext.Provider
      value={{ lang, setLang, t, browserPrefersSpanish, shouldShowBanner, dismissBanner }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
